// src/utils/attributionReader.ts
import { consentAllows } from '@common/services/consent/gate';
import { isExtensionContext } from '@common/services/detect-context';
import { currentConsent } from '@common/services/consent/store';
import { currentJurisdiction, hasGlobalPrivacyControl } from '@common/services/consent/jurisdiction';
import { buildBridgedSignupConsent, buildSignupConsent, type SignupConsent } from '@common/services/consent/signup-consent';

// Module-level cache for attribution data from chrome.storage.local
// Preloaded during extension init so readAttributionCookie() stays synchronous
let _cachedAttribution: AttributionData | null = null;
let _cacheLoaded = false;
// #1023: blueticks.co cookies the extension's attribution bridge copied into chrome.storage
// (`bt_attribution_cookies`): the Meta browser ids and the consent/geo cookies.
let _cachedBridgeCookies: BridgedCookies = {};

/** The subset of `bt_attribution_cookies` the signup reads. Values are already URI-decoded by the bridge. */
export interface BridgedCookies {
  _fbp?: string;
  _fbc?: string;
  bt_consent?: string;
  bt_geo?: string;
}

const BRIDGED_COOKIE_KEYS: readonly (keyof BridgedCookies)[] = ['_fbp', '_fbc', 'bt_consent', 'bt_geo'];

/** Pure: picks the string values this module uses out of the stored JSON. Never throws. */
export function parseBridgedCookies(raw: unknown): BridgedCookies {
  if (typeof raw !== 'string') return {};
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof data !== 'object' || data === null) return {};
  const out: BridgedCookies = {};
  for (const key of BRIDGED_COOKIE_KEYS) {
    const value: unknown = Reflect.get(data, key);
    if (typeof value === 'string' && value.length > 0) out[key] = value;
  }
  return out;
}

/**
 * Multi-Touch Attribution Data Interface
 * Matches the format used in attribution.ts
 */
export interface AttributionData {
  // First-touch attribution for internal analytics
  first_touch: {
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_content?: string | null;
    utm_term?: string | null;
    landing_page?: string | null;
    timestamp: string;
    page_url: string;
    referrer: string;
  };

  // Multi-touch conversion tracking - ALL unique click IDs
  conversion_ids: {
    gclids: string[];   // All Google Ads click IDs
    fbclids: string[];  // All Facebook Ads click IDs
    msclkids: string[]; // All Microsoft Ads click IDs
    ttclids: string[];  // All TikTok Ads click IDs
  };

  // Google Analytics Client ID
  ga_client_id?: string | null;

  // Google Analytics Session ID (for MP event session matching)
  ga_session_id?: string | null;

  // Stable visitor ID (browser fingerprint) for cross-platform tracking
  stable_visitor_id?: string;

  // Meta browser ids (_fbp/_fbc) read at send time; only present once the pixel has set them (i.e. after consent)
  meta_ids?: MetaBrowserIds;

  // #1023: the visitor's marketing-consent decision, stored server-side as users.marketingConsent
  consent?: SignupConsent;

  // Last update timestamp
  last_updated: string;
}

/** Meta pixel cookie values the backend needs for CAPI matching (#855). */
export interface MetaBrowserIds {
  fbp?: string;
  fbc?: string;
}

/**
 * Legacy attribution format for backwards compatibility
 * @deprecated Use AttributionData (multi-touch format) instead
 */
export interface LegacyAttributionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  ga_client_id: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  msclkid?: string | null;
  ttclid?: string | null;
  landing_page?: string | null;
  timestamp: string;
  page_url: string;
  referrer: string;
}

/**
 * Preloads attribution data from chrome.storage.local into the module cache.
 * Call this early during extension init (before any signup flow) so that
 * readAttributionCookie() can synchronously return cached data when the
 * blueticks_attribution cookie is not accessible (e.g. on web.whatsapp.com).
 */
export async function preloadAttributionCache(): Promise<void> {
  if (_cacheLoaded) return;
  try {
    if (typeof chrome === 'undefined' || !chrome?.storage?.local) {
      _cacheLoaded = true;
      return;
    }
    // Race against a timeout so a slow/stuck storage read never blocks extension init
    const result = await Promise.race([
      chrome.storage.local.get(['bt_attribution_data', 'bt_attribution_ga_client_id', 'bt_attribution_cookies']),
      new Promise<Record<string, never>>((resolve) => setTimeout(() => resolve({}), 2000)),
    ]);
    _cachedBridgeCookies = parseBridgedCookies(result.bt_attribution_cookies);
    if (result.bt_attribution_data) {
      const parsedData = JSON.parse(result.bt_attribution_data);
      const isNewFormat = parsedData.first_touch && parsedData.conversion_ids;
      if (isNewFormat) {
        _cachedAttribution = parsedData as AttributionData;
      } else {
        const legacy = parsedData as LegacyAttributionData;
        _cachedAttribution = convertLegacyAttribution(legacy);
      }
      // Overlay GA client ID from storage if available and not already set
      if (_cachedAttribution && !_cachedAttribution.ga_client_id && result.bt_attribution_ga_client_id) {
        _cachedAttribution.ga_client_id = result.bt_attribution_ga_client_id;
      }
      console.log('[Attribution] Preloaded attribution from chrome.storage', _cachedAttribution?.first_touch?.utm_source);
    }
  } catch (e) {
    console.warn('[Attribution] Failed to preload from chrome.storage:', e);
  } finally {
    _cacheLoaded = true;
  }
}

/**
 * Converts legacy attribution format to the new multi-touch format.
 */
function convertLegacyAttribution(legacyData: LegacyAttributionData): AttributionData {
  return {
    first_touch: {
      utm_source: legacyData.utm_source,
      utm_medium: legacyData.utm_medium,
      utm_campaign: legacyData.utm_campaign,
      utm_content: legacyData.utm_content,
      utm_term: legacyData.utm_term,
      landing_page: legacyData.landing_page || null,
      timestamp: legacyData.timestamp || new Date().toISOString(),
      page_url: legacyData.page_url,
      referrer: legacyData.referrer,
    },
    conversion_ids: {
      gclids: legacyData.gclid ? [legacyData.gclid] : [],
      fbclids: legacyData.fbclid ? [legacyData.fbclid] : [],
      msclkids: legacyData.msclkid ? [legacyData.msclkid] : [],
      ttclids: legacyData.ttclid ? [legacyData.ttclid] : [],
    },
    ga_client_id: legacyData.ga_client_id,
    last_updated: new Date().toISOString(),
  };
}

/**
 * Reads and parses the 'blueticks_attribution' cookie.
 * Handles both new multi-touch format and legacy format.
 * Falls back to chrome.storage cache when the cookie is not accessible
 * (e.g. extension running on web.whatsapp.com).
 * @returns {AttributionData | null} The parsed data or null if not found/invalid.
 */
const META_COOKIE_PATTERNS: Record<keyof MetaBrowserIds, RegExp> = {
  fbp: /(?:^|;\s*)_fbp=([^;]+)/,
  fbc: /(?:^|;\s*)_fbc=([^;]+)/,
};

// Shapes Meta writes: fb.<subdomainIndex>.<creationTimeMs>.<fbclid | random id>. Same checks as the backend.
const META_VALUE_FORMATS: Record<keyof MetaBrowserIds, RegExp> = {
  fbp: /^fb\.\d\.\d{10,13}\.\d{4,20}$/,
  fbc: /^fb\.\d\.\d{10,13}\.[A-Za-z0-9_-]{8,512}$/,
};

/**
 * Pure: extracts the Meta pixel cookies from a `document.cookie` string. Absent, malformed
 * (bad percent-encoding) or wrongly shaped values are omitted; this never throws (it runs on the signup path).
 */
export function readMetaBrowserIds(cookieString: string): MetaBrowserIds {
  const ids: MetaBrowserIds = {};
  for (const key of ['fbp', 'fbc'] as const) {
    const match = cookieString.match(META_COOKIE_PATTERNS[key]);
    if (!match) continue;
    try {
      const value = decodeURIComponent(match[1]);
      if (META_VALUE_FORMATS[key].test(value)) ids[key] = value;
    } catch {
      // malformed percent sequence: skip this value
    }
  }
  return ids;
}

/** Pure: the Meta ids among the bridged blueticks.co cookies, same shape checks as the cookie reader. */
export function metaIdsFromBridge(bridged: BridgedCookies): MetaBrowserIds {
  const ids: MetaBrowserIds = {};
  if (bridged._fbp && META_VALUE_FORMATS.fbp.test(bridged._fbp)) ids.fbp = bridged._fbp;
  if (bridged._fbc && META_VALUE_FORMATS.fbc.test(bridged._fbc)) ids.fbc = bridged._fbc;
  return ids;
}

/**
 * Pure: adds the signup-only fields to an attribution payload. Meta ids travel only when the consent in
 * effect grants marketing.
 */
export function withSignupSignals(
  attribution: AttributionData,
  consent: SignupConsent | undefined,
  metaIds: MetaBrowserIds,
  marketingAllowed: boolean,
): AttributionData {
  const out: AttributionData = consent ? { ...attribution, consent } : attribution;
  if (!marketingAllowed || !(metaIds.fbp || metaIds.fbc)) return out;
  return { ...out, meta_ids: metaIds };
}

/** The signup-only fields: the consent decision in effect and, when marketing is allowed, the Meta browser ids. */
export interface SignupSignals {
  consent?: SignupConsent;
  marketingAllowed: boolean;
  metaIds: MetaBrowserIds;
}

/**
 * Reads the signup-only signals for the current context, independent of any attribution payload.
 * - Web app: the consent cookie / jurisdiction default, and `_fbp`/`_fbc` from document.cookie.
 * - Extension: the copies the attribution bridge took from blueticks.co (document.cookie there is WhatsApp's).
 * Never throws; returns an empty, marketing-denied result when nothing is readable.
 */
export function readSignupSignals(): SignupSignals {
  if (typeof document === 'undefined') return { marketingAllowed: false, metaIds: {} };
  try {
    const now = new Date();
    if (isExtensionContext()) {
      const consent = buildBridgedSignupConsent({
        bridgedConsent: _cachedBridgeCookies.bt_consent,
        bridgedGeo: _cachedBridgeCookies.bt_geo,
        gpc: hasGlobalPrivacyControl(),
        now,
      });
      return { consent, marketingAllowed: consent?.marketing === true, metaIds: metaIdsFromBridge(_cachedBridgeCookies) };
    }
    const consent = buildSignupConsent({ stored: currentConsent(), decision: currentJurisdiction(), source: 'web', now });
    return { consent, marketingAllowed: consentAllows('marketing'), metaIds: readMetaBrowserIds(document.cookie) };
  } catch {
    return { marketingAllowed: false, metaIds: {} };
  }
}

/** Pure: {@link withSignupSignals} for a read {@link SignupSignals}. */
export function applySignupSignals(attribution: AttributionData, signals: SignupSignals): AttributionData {
  return withSignupSignals(attribution, signals.consent, signals.metaIds, signals.marketingAllowed);
}

/**
 * Same as {@link readAttributionBase} plus the signup-only fields: `consent` (#1023) and the Meta browser ids
 * (`meta_ids`, #855), so every attribution payload sent to the backend (email signup, Google signup PATCH)
 * carries them for server-side CAPI.
 */
export function readAttributionCookie(): AttributionData | null {
  const attribution = readAttributionBase();
  if (!attribution || typeof document === 'undefined') return attribution;
  try {
    return applySignupSignals(attribution, readSignupSignals());
  } catch {
    return attribution;
  }
}

function readAttributionBase(): AttributionData | null {
  try {
    // Ensure this code runs only in the browser
    if (typeof document === 'undefined') {
      return _cachedAttribution;
    }

    const cookieMatch = document.cookie.match(/blueticks_attribution=([^;]+)/);
    if (!cookieMatch) {
      // Cookie not found — fall back to chrome.storage cache (extension context)
      return _cachedAttribution;
    }

    const decodedValue = decodeURIComponent(cookieMatch[1]);
    const parsedData = JSON.parse(decodedValue);

    // Check if it's the new multi-touch format
    const isNewFormat = parsedData.first_touch && parsedData.conversion_ids;

    if (isNewFormat) {
      // Already in the correct format
      return parsedData as AttributionData;
    } else {
      // Legacy format - convert to new format on the fly
      return convertLegacyAttribution(parsedData as LegacyAttributionData);
    }
  } catch (error) {
    console.warn('Failed to read or parse attribution cookie:', error);
    return _cachedAttribution;
  }
}

/**
 * Optional: Clears the attribution cookie after it has been processed.
 */
export function clearAttributionCookie(): void {
  if (typeof document === 'undefined') return;
  // Set expiry date to the past and specify the shared domain
  document.cookie = 'blueticks_attribution=; path=/; domain=.blueticks.co; expires=Thu, 01 Jan 1970 00:00:00 GMT';
}
